// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { type FC, type Dispatch, type SetStateAction, useState } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableRow } from './table-row';
import { useConst } from '@shared/ui-kit/hooks/use-const';

export interface TablePositionsProps {
  excelData: PortfolioLoaded[];

  setExcelData: Dispatch<SetStateAction<PortfolioLoaded[]>>;

  updateMyData(rowIndex: number, columnId: string, value: any): void;

  invalidPositions?: string[];

  mode: 'weight' | 'outstanding_balance';

  totalWeight: number;
}

export const TablePositions: FC<TablePositionsProps> = ({
  excelData,
  setExcelData: setData,
  updateMyData,
  invalidPositions,
  mode,
  totalWeight,
}) => {
  const { t } = useTranslation();
  const headers = useMemo(
    () => [
      t('portfolio.ticker-isin', { ns: 'components' }),
      mode === 'weight'
        ? t('portfolio.weight', { ns: 'components' })
        : t('glossary.investment', { ns: 'glossary' }),
      t('glossary.capital-gains', { ns: 'glossary' }),
      t('portfolio.acquisition-date', { ns: 'components' }),
    ],
    [mode, t],
  );

  const currentDate = useConst(() => new Date().toISOString().split('T')[0]);

  const [initialValue, setInitialValue] = useState<Required<PortfolioLoaded>>({
    ticker: '',
    weight: 0,
    outstanding_balance: 0,
    unrealized_capital_gains: 0,
    date: currentDate,
    selectedAsset: false,
  });

  const data = useMemo(
    () =>
      excelData.map(value => ({
        ...value,
        date: new Date(value?.date ?? Date.now()).toISOString().split('T')[0],
      })),
    [excelData],
  );

  return (
    <div
      className="relative mt-4 grid [grid-auto-rows:minmax(24px,auto)]"
      data-testid="table-positions"
    >
      <div className="sticky top-0 z-[1] grid grid-cols-[repeat(4,minmax(120px,1fr))_20px] gap-2.5 !bg-section">
        {Array.from(headers, (title, key) => (
          <span key={key} className="font-normal uppercase text-primary-medium">
            {title}
          </span>
        ))}
        <div />
      </div>
      <TableRow
        id={-1}
        initialValue={initialValue}
        portfolio={initialValue}
        setData={setData}
        setInitialValue={setInitialValue}
        updateMyData={updateMyData}
        autocomplete
        mode={mode}
        totalWeight={totalWeight}
      />
      {Array.from(data, (portfolio, key) => (
        <TableRow
          key={key}
          id={key}
          initialValue={initialValue}
          portfolio={portfolio}
          setData={setData}
          setInitialValue={setInitialValue}
          updateMyData={updateMyData}
          invalidPositions={invalidPositions}
          mode={mode}
          totalWeight={totalWeight}
        />
      ))}
    </div>
  );
};
